import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import {
  User as FirebaseUser,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  updateProfile,
} from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '@/lib/firebase';
import { User } from '@/types';

// ─── Inactivity auto-logout (4h) ─────────────────────────────────────────────
const INACTIVITY_KEY = 'cf_last_active_at';
const INACTIVITY_TIMEOUT_MS = 4 * 60 * 60 * 1000; // 4 hours

function touchActivity() {
  try { localStorage.setItem(INACTIVITY_KEY, String(Date.now())); } catch {}
}
function clearActivity() {
  try { localStorage.removeItem(INACTIVITY_KEY); } catch {}
}
function isSessionExpired(): boolean {
  try {
    const stored = localStorage.getItem(INACTIVITY_KEY);
    if (!stored) return false;
    const last = Number(stored);
    if (!Number.isFinite(last)) return false;
    return Date.now() - last > INACTIVITY_TIMEOUT_MS;
  } catch { return false; }
}

interface AuthContextType {
  user: FirebaseUser | null;
  userProfile: User | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string, phone?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [userProfile, setUserProfile] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  async function loadProfile(firebaseUser: FirebaseUser) {
    const snap = await getDoc(doc(db, 'users', firebaseUser.uid));
    if (snap.exists()) setUserProfile(snap.data() as User);
  }

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Auto-logout after 48h of inactivity.
        if (isSessionExpired()) {
          clearActivity();
          await signOut(auth);
          setUser(null);
          setUserProfile(null);
          setLoading(false);
          return;
        }
        touchActivity();
        setUser(firebaseUser);
        await loadProfile(firebaseUser);
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  // Re-check inactivity on tab focus / visibility change, and bump the timestamp on hide.
  useEffect(() => {
    const onVisibility = async () => {
      if (document.visibilityState === 'visible') {
        if (auth.currentUser && isSessionExpired()) {
          clearActivity();
          await signOut(auth);
          setUser(null);
          setUserProfile(null);
        } else if (auth.currentUser) {
          touchActivity();
        }
      } else if (auth.currentUser) {
        touchActivity();
      }
    };
    const onFocus = () => { if (auth.currentUser) touchActivity(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('beforeunload', () => { if (auth.currentUser) touchActivity(); });
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
    touchActivity();
  };

  const signUp = async (email: string, password: string, displayName: string, phone?: string) => {
    const { user: newUser } = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(newUser, { displayName });

    const userDoc: User = {
      id: newUser.uid,
      email: newUser.email!,
      displayName,
      createdAt: new Date().toISOString(),
      kycStatus: 'pending',
      ...(phone ? { phoneNumber: phone } : {}),
    };

    await setDoc(doc(db, 'users', newUser.uid), { ...userDoc, createdAt: serverTimestamp() });

    // Fire-and-forget our custom verification email (CardFlow-branded, sent via SMTP).
    fetch('/api/auth/send-verification', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: newUser.uid, email: newUser.email }),
    }).catch((err) => console.error('Failed to send verification email:', err));
    await setDoc(doc(db, 'wallets', newUser.uid), {
      userId: newUser.uid,
      nairaBalance: 0,
      usdBalance: 0,
      virtualAccount: {
        bankName: 'Wema Bank',
        accountNumber: `${7000000000 + Math.floor(Math.random() * 999999999)}`,
        accountName: `CardFlow / ${displayName}`,
        reference: `CF-${newUser.uid.slice(0, 8).toUpperCase()}`,
      },
      updatedAt: serverTimestamp(),
    });

    touchActivity();
    setUserProfile(userDoc);
  };

  const logout = async () => {
    clearActivity();
    await signOut(auth);
    setUserProfile(null);
  };

  const refreshProfile = async () => {
    if (user) await loadProfile(user);
  };

  return (
    <AuthContext.Provider value={{ user, userProfile, loading, signIn, signUp, logout, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
